import { makeAutoObservable, action } from 'mobx';
import api from '@/utils/api';

class FavoritesStore {
  favorites = [];
  isLoading = false;
  error = null;
  syncInProgress = false;
  hasInitialized = false;
  initializationPromise = null;

  constructor() {
    makeAutoObservable(this, {
      setFavorites: action,
      addToFavorites: action,
      removeFromFavorites: action,
      loadFavorites: action,
      syncWithServer: action,
    });

    // Initialize favorites from local storage
    if (typeof window !== 'undefined') {
      const storedFavorites = this.getLocalFavorites();
      if (storedFavorites.length > 0) {
        this.favorites = storedFavorites;
        this.hasInitialized = true;
      } else if (localStorage.getItem('token')) {
        // Only fetch from server if local storage is empty and user is logged in
        // Don't call fetchFromServer directly here to avoid duplicate calls
        // Let loadFavorites() handle the initialization
      }
    }
  }

  // Get favorites from local storage
  getLocalFavorites() {
    if (typeof window !== 'undefined') {
      try {
        const storedFavorites = localStorage.getItem('favorites');
        return storedFavorites ? JSON.parse(storedFavorites) : [];
      } catch (error) {
        console.error('Error parsing favorites from localStorage:', error);
        return [];
      }
    }
    return [];
  }

  // Save favorites to local storage
  saveLocalFavorites(favorites) {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('favorites', JSON.stringify(favorites));
      } catch (error) {
        console.error('Error saving favorites to localStorage:', error);
      }
    }
  }

  setFavorites(favorites) {
    // Ensure favorites is always an array
    const favoritesArray = Array.isArray(favorites) ? favorites : [];
    this.favorites = favoritesArray;
    this.saveLocalFavorites(favoritesArray);
  }

  setLoading(loading) {
    this.isLoading = loading;
  }

  setError(error) {
    this.error = error;
  }

  get favoritesCount() {
    return this.favorites.length;
  }

  isFavorite(productId) {
    return Array.isArray(this.favorites) &&
           this.favorites.some(fav => Number(fav.id) === Number(productId));
  }

  // Sync local favorites with server (called periodically or after changes)
  async syncWithServer() {
    if (this.syncInProgress || typeof window === 'undefined' || !localStorage.getItem('token')) {
      return;
    }

    this.syncInProgress = true;
    try {
      const response = await api.get('/favorites');

      if (response && response.data) {
        const serverFavorites = Array.isArray(response.data) ? response.data : [];
        const localFavorites = this.getLocalFavorites();

        // Compare and merge local and server favorites
        // This implementation preserves local changes and server data
        const localIds = new Set(localFavorites.map(fav => Number(fav.id)));
        const serverIds = new Set(serverFavorites.map(fav => Number(fav.id)));

        // Items in local but not in server - need to be added to server
        const localOnly = localFavorites.filter(fav => !serverIds.has(Number(fav.id)));

        // Items in server but not in local - need to be added to local
        const serverOnly = serverFavorites.filter(fav => !localIds.has(Number(fav.id)));

        // Process differences
        if (localOnly.length > 0) {
          // Add local favorites to server
          for (const fav of localOnly) {
            await api.post(`/favorites/add/${fav.id}`);
          }
        }

        // Update local storage with full data from server plus any new local items
        if (serverOnly.length > 0) {
          const updatedFavorites = [...localFavorites, ...serverOnly];
          this.setFavorites(updatedFavorites);
        }
      }
    } catch (error) {
      console.error('Error syncing favorites with server:', error);
      // Don't update local storage on sync error to preserve user's local state
    } finally {
      this.syncInProgress = false;
    }
  }

  // Load favorites from server (used at login or initial load)
  async fetchFromServer() {
    try {
      this.setLoading(true);
      this.setError(null);
      const response = await api.get('/favorites');

      // Set favorites, ensuring it's an array
      if (response && response.data) {
        this.setFavorites(response.data);
      } else {
        this.setFavorites([]);
      }
    } catch (error) {
      console.error('Error loading favorites from server:', error);
      this.setError('Failed to load favorites');
    } finally {
      this.setLoading(false);
    }
  }

  // Legacy method for compatibility - now with deduplication
  async loadFavorites() {
    // If already initialized, just return the existing promise or current state
    if (this.hasInitialized) {
      return this.initializationPromise || Promise.resolve();
    }

    // If initialization is already in progress, return the existing promise
    if (this.initializationPromise) {
      return await this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this._initializeFavorites();
    return await this.initializationPromise;
  }

  // Private method to handle initialization
  async _initializeFavorites() {
    if (this.hasInitialized) {
      return;
    }

    // If we already have favorites locally, just trigger a background sync
    if (this.favorites.length > 0) {
      this.syncWithServer();
      this.hasInitialized = true;
    } else {
      // Otherwise, fetch from server
      await this.fetchFromServer();
      // Only mark initialized if no error occurred
      if (!this.error) {
        this.hasInitialized = true;
      }
    }
  }

  addToFavorites(product) {
    try {

      // Add to local favorites immediately
      const updatedFavorites = [...this.favorites, product];
      this.setFavorites(updatedFavorites);

      // Update server in background
      api.post(`/favorites/add/${product.id}`).then(() => {
        // After successful server update, sync to get full product details
        this.syncWithServer();
      }).catch(error => {
        console.error('Error adding to favorites on server:', error);
        // Remove from local if server update fails
        this.setFavorites(this.favorites.filter(fav => Number(fav.id) !== Number(product.id)));
      });

      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return false;
    }
  }

  removeFromFavorites(productId) {
    try {
      // Remove from local favorites immediately
      const updatedFavorites = this.favorites.filter(fav => Number(fav.id) !== Number(productId));
      this.setFavorites(updatedFavorites);

      // Update server in background
      api.delete(`/favorites/remove/${productId}`).catch(error => {
        console.error('Error removing from favorites on server:', error);
        // If the server update fails, we could add it back to local,
        // but for better UX let's keep it removed and sync later
      });

      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return false;
    }
  }

  toggleFavorite(product) {
    if (this.isFavorite(product.id)) {
      return this.removeFromFavorites(product.id);
    } else {
      return this.addToFavorites(product);
    }
  }

  // Reset initialization state (useful when user logs out or token changes)
  resetInitialization() {
    this.hasInitialized = false;
    this.initializationPromise = null;
  }
}

const favoritesStore = new FavoritesStore();
export default favoritesStore;